import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthenticationApiService } from '@modules/authentication/api/authentication.api.service';
import { AuthAppRedirectService } from '@services/auth-app-redirect.service';

@Injectable({
  providedIn: 'root',
})
export class RefreshAccessTokenService implements OnDestroy {
  private isRefreshing = false;
  private readonly isTokenRefreshCompleted$ = new BehaviorSubject<boolean>(false);

  private readonly authenticationApiService = inject(AuthenticationApiService);
  private readonly authAppRedirectService = inject(AuthAppRedirectService);

  attemptRefresh = (clonedReq: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (this.isRefreshing) {
      return this.waitForRefreshAndRetryRequest(clonedReq, next);
    }

    return this.performTokenRefresh(clonedReq, next);
  };

  private waitForRefreshAndRetryRequest(
    clonedReq: HttpRequest<unknown>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> {
    return this.isTokenRefreshCompleted$.pipe(
      filter((isTokenRefreshCompleted) => isTokenRefreshCompleted === true),
      take(1),
      switchMap(() => this.retryRequestWithCredentials(clonedReq, next)),
    ) as Observable<HttpEvent<unknown>>;
  }

  private performTokenRefresh(clonedReq: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    this.isRefreshing = true;
    this.isTokenRefreshCompleted$.next(false);

    return this.authenticationApiService.refreshAccessToken().pipe(
      // Only a failed refresh ends the session: an error of the retried request is the caller's to handle.
      catchError((error) => this.handleRefreshError(error)),
      switchMap(() => {
        // refresh successful: notify waiters and retry original request with cookies
        this.isRefreshing = false;
        this.isTokenRefreshCompleted$.next(true);
        return this.retryRequestWithCredentials(clonedReq, next);
      }),
      finalize(() => {
        if (this.isRefreshing) {
          this.resetRefreshState();
        }
      }),
    ) as Observable<HttpEvent<unknown>>;
  }

  private retryRequestWithCredentials(
    clonedReq: HttpRequest<unknown>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> {
    return next(clonedReq.clone({ withCredentials: true })).pipe(
      catchError((error) => {
        // Still unauthorized with a fresh token: the session is unusable.
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
          return this.handleRefreshError(error);
        }
        return throwError(() => error);
      }),
    );
  }

  private handleRefreshError(error: unknown): Observable<HttpEvent<unknown>> {
    this.resetRefreshState();
    this.authAppRedirectService.login();
    return throwError(() => error) as Observable<HttpEvent<unknown>>;
  }

  private resetRefreshState(): void {
    this.isRefreshing = false;
    this.isTokenRefreshCompleted$.next(false);
  }

  ngOnDestroy(): void {
    this.isTokenRefreshCompleted$.complete();
  }
}
